import { LegendItem } from './LegendItem.component';
import type { ReactNode } from 'react';
import React, { ComponentPropsWithRef, FC, useMemo } from 'react';
import { Text } from '@components/Text/Text.component';
import { PlcAccordion } from '@layout/components/PlcAccordion/PlcAccordion.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import classnames from 'classnames';

export type LegendOption = { icon: ReactNode; labelI18n?: string; label?: string };

const LegendBlock: FC<
  { legendOptions: LegendOption[][]; withBackgroundAndTitle?: boolean } & Omit<ComponentPropsWithRef<'div'>, 'onChange'>
> = ({ legendOptions, className, withBackgroundAndTitle = true, ...rest }) => {
  const isMobile = useIsBreakpointDown(Breakpoint.l);

  const body = useMemo(
    () =>
      legendOptions.map((row, rowIndex) => (
        <div className={rowIndex === legendOptions.length - 1 ? undefined : 'plc-mr-xl'} key={rowIndex}>
          {row.map((item, index) => (
            <LegendItem
              key={item.labelI18n || item.label}
              {...item}
              className={index === row.length - 1 ? undefined : 'plc-mb-s'}
            />
          ))}
        </div>
      )),
    []
  );

  const combineItems = useMemo(
    () =>
      legendOptions.map((row, rowIndex) =>
        row.map((item, index) => (
          <LegendItem
            key={`${rowIndex}-${item.labelI18n}`}
            {...item}
            className={index === row.length - 1 && rowIndex === legendOptions.length - 1 ? undefined : 'plc-mb-s'}
          />
        ))
      ),
    []
  );

  const title = useMemo(() => <Text i18nKey="common.legend" variant="h5" />, []);

  if (isMobile) {
    const rootClass = classnames('mobile-legend-container', 'flex', 'flex-1', 'column', className);
    return (
      <PlcAccordion className={rootClass} titleComponent={title} {...rest}>
        <div>{combineItems}</div>
      </PlcAccordion>
    );
  }

  const rootClass = classnames(
    'desktop-legend-container',
    'plc-p-basis',
    withBackgroundAndTitle && 'background-ultralight',
    className
  );
  return (
    <div className={rootClass} {...rest}>
      {withBackgroundAndTitle && <header className="plc-mb-m">{title}</header>}
      <article className="flex">{body}</article>
    </div>
  );
};

export { LegendBlock };
